import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { apiConfigResolver } from '@df/api/config/api-config.resolver';
import { countriesListResolver } from '@df/api/config/contries-list.resolver';
import { basketItemsStockReloadResolver } from '@df/basket/stock-reload.resolver';
import { advancedCommerceRunResolvers } from '@df/module-advanced-commerce';
import { AdvancedCommerceConfigStore } from '@df/module-advanced-commerce/store/advanced-commerce-config.store';
import { CustomerReloadResolver } from '@df/session/customer/customer-reload.resolver';
import { DevOnlyGuard } from '@df/ui/dev-only.guard';
import { PlainRouterOutletComponent } from '@df/util/plain-router-outlet.component';
import { icmsMetaRoutes } from '@icms/client/icms-client.routes';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { isInAppCustomerGuard } from './account/guards/is-in-app-customer.guard';
import { isStandaloneGuard } from './account/guards/is-standalone.guard';
import { horseEditResolver } from './account/pwa/profiles/horse-edit/horse-edit.resolver';
import { memberEditResolver } from './account/pwa/profiles/member-edit/member-edit.resolver';
import { AppRootComponent } from './app-root.component';
import { BasketViewComponent } from './basket/basket-view.component';
import { Error500Component } from './catalog/ui/error-500.component';
import { LocalCustomerNotLoggedGuard } from './customer/guards/local-customer-not-logged.guard';
import { LoginViewComponent } from './login/view/login-view.component';
import { FirstMissionIntroductionResolver } from './module/pwa/first-mission-introduction/first-mission-introduction.resolver';
import { firstMissionResolver } from './module/pwa/first-mission/first-mission.resolver';
import { pwaCustomerLoggedGuard } from './module/pwa/guards/pwa-customer-logged.guard';
import { profileMissionResolver } from './module/pwa/profile-mission/profile-mission.resolver';
import { SignInAccessLayoutResolver } from './module/pwa/sign-in-access/sign-in-access-layout.resolver';
import { pwaTutorialModalResolver } from './module/pwa/tutorial-screen/pwa-tutorial-modal.resolver';
import { DynamicSearchComponent } from './search/dynamic-search.component';
import { dynamicSearchResolver } from './search/search.resolver';

export const appRoutes: Routes = [
    {
        path: '_library',
        canActivate: [DevOnlyGuard],
        resolve: {
            appData: apiConfigResolver
        },
        loadChildren: () =>
            import(
                /* webpackChunkName: "dfd.aw" */
                './module/df-demo/dfd.module'
            ).then(m => m.DfdModule)
    },
    ...icmsMetaRoutes,
    {
        path: '',
        component: AppRootComponent,
        resolve: {
            appData: apiConfigResolver,
            acSwitch: () => inject(AdvancedCommerceConfigStore)?.loadConfig(),
            tutorialModal: pwaTutorialModalResolver,
            header: () => inject(ICmsBlockResource).retrieve({ bareUrl: 'page-header' }, undefined),
            footer: () => inject(ICmsBlockResource).retrieve({ bareUrl: 'page-footer' }, undefined)
        },
        children: [
            {
                path: 'sign-in-access',
                resolve: {
                    cmsBlock: SignInAccessLayoutResolver
                },
                canActivate: [isStandaloneGuard],
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "sign-in-access.async" */
                        './module/pwa/sign-in-access/sign-in-access-layout.component'
                    ).then(m => m.SignInAccessLayoutComponent)
            },
            {
                path: 'first-mission-introduction',
                canActivate: [isInAppCustomerGuard],
                resolve: {
                    firstMission: firstMissionResolver,
                    cmsBlock: FirstMissionIntroductionResolver
                },
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "first-mission-introduction.async" */
                        './module/pwa/first-mission-introduction/first-mission-introduction.component'
                    ).then(m => m.FirstMissionIntroductionComponent)
            },
            {
                path: 'first-mission',
                canActivate: [pwaCustomerLoggedGuard],
                resolve: {
                    firstMission: firstMissionResolver
                },
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "first-mission.async" */
                        './module/pwa/first-mission/first-mission.component'
                    ).then(m => m.FirstMissionComponent)
            },
            {
                path: 'profile-mission',
                canActivate: [pwaCustomerLoggedGuard],
                resolve: {
                    profileMission: profileMissionResolver,
                    memberEdit: memberEditResolver,
                    horseEdit: horseEditResolver,
                    appMore: countriesListResolver
                },
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "profile-mission.async" */
                        './module/pwa/profile-mission/profile-mission.component'
                    ).then(m => m.ProfileMissionComponent)
            },
            {
                path: 'account',
                canActivate: [pwaCustomerLoggedGuard],
                loadChildren: () =>
                    import(
                        /* webpackChunkName: "account.async" */
                        './account/account.routes'
                    ).then(m => m.accountRoutes)
            },
            {
                path: 'login',
                component: LoginViewComponent,
                canActivate: [LocalCustomerNotLoggedGuard],
                loadChildren: () =>
                    import(
                        /* webpackChunkName: "login.async" */
                        './login/login.routes'
                    ).then(m => m.loginRoutes)
            },
            {
                path: 'basket',
                component: BasketViewComponent,
                resolve: {
                    customer: CustomerReloadResolver,
                    stock: basketItemsStockReloadResolver
                }
            },
            {
                path: 'checkout',
                loadChildren: () =>
                    import(
                        /* webpackChunkName: "checkout.async" */
                        './checkout/checkout.routes'
                    ).then(m => m.checkoutRoutes)
            },
            {
                path: 'search',
                component: PlainRouterOutletComponent,
                children: [
                    {
                        path: '**',
                        component: DynamicSearchComponent,
                        resolve: {
                            searchResult: dynamicSearchResolver
                        },
                        runGuardsAndResolvers: advancedCommerceRunResolvers
                    }
                ]
            },
            {
                path: 'error',
                component: Error500Component
            },
            {
                path: '**',
                loadChildren: () =>
                    import(
                        /* webpackChunkName: "catalog.async" */
                        './catalog/catalog.routes'
                    ).then(m => m.catalogRoutes)
            }
        ]
    }
];
